import type React from "react";

import type { CloudinaryUploadModalProps } from "./CloudinaryUploadModal/CloudinaryUploadModal.types";
import type { HomeCompactSearchTrayProps } from "./HomeCompactSearchTray/HomeCompactSearchTray.types";
import type { HomeHeroFieldTrayProps } from "./HomeHeroFieldTray/HomeHeroFieldTray.types";
import type { HomePromoEntitlementsModalProps } from "./HomePromoEntitlementsModal/HomePromoEntitlementsModal.types";
import type { HostInterstitialModalProps } from "./HostInterstitialModal/HostInterstitialModal.types";
import type { ImageGalleryModalProps } from "./ImageGalleryModal/ImageGalleryModal.types";
import type { ListingAddOnModalProps } from "./ListingAddOnModal/ListingAddOnModal.types";
import type { ListingAiAnswersModalProps } from "./ListingAiAnswersModal/ListingAiAnswersModal.types";
import type { ListingAwardsWinnerDrawerProps } from "./ListingAwardsWinnerDrawer/ListingAwardsWinnerDrawer.types";
import type { ListingFsvMenuModalProps } from "./ListingFsvMenuModal/ListingFsvMenuModal.types";
import type { ListingPhotoDownloadModalProps } from "./ListingPhotoDownloadModal/ListingPhotoDownloadModal.types";
import type { ListingPriceDetailsDrawerProps } from "./ListingPriceDetailsDrawer/ListingPriceDetailsDrawer.types";
import type { ListingPriceDetailsModalProps } from "./ListingPriceDetailsModal/ListingPriceDetailsModal.types";
import type { ListingPricingRulesModalProps } from "./ListingPricingRulesModal/ListingPricingRulesModal.types";
import type { ListingRecommendationsModalProps } from "./ListingRecommendationsModal/ListingRecommendationsModal.types";
import type { ListingSaveModalProps } from "./ListingSaveModal/ListingSaveModal.types";
import type { PendingReviewsModalProps } from "./PendingReviewsModal/PendingReviewsModal.types";
import type { PhoneVerificationModalProps } from "./PhoneVerificationModal/PhoneVerificationModal.types";
import type { PhotoViewerModalProps } from "./PhotoViewerModal/PhotoViewerModal.types";
import type { ProfileModalProps } from "./ProfileModal/ProfileModal.types";
import type { ProfileNameModalProps } from "./ProfileNameModal/ProfileNameModal.types";
import type { ProfileOccupationModalProps } from "./ProfileOccupationModal/ProfileOccupationModal.types";
import type { ProfilesMessageHostModalProps } from "./ProfilesMessageHostModal/ProfilesMessageHostModal.types";
import type { ShareModalProps } from "./ShareModal/ShareModal.types";
import type { VendorAboutModalProps } from "./VendorAboutModal/VendorAboutModal.types";
import type { VendorAddVideosModalProps } from "./VendorAddVideosModal/VendorAddVideosModal.types";
import type { VendorContactSheetProps } from "./VendorContactSheet/VendorContactSheet.types";
import type { VendorDeleteMediaModalProps } from "./VendorDeleteMediaModal/VendorDeleteMediaModal.types";
import type { VendorDocumentModalProps } from "./VendorDocumentModal/VendorDocumentModal.types";
import type { VendorMediaTypeModalProps } from "./VendorMediaTypeModal/VendorMediaTypeModal.types";
import type { VendorPricingModalProps } from "./VendorPricingModal/VendorPricingModal.types";
import type { VendorReplacePhotoModalProps } from "./VendorReplacePhotoModal/VendorReplacePhotoModal.types";
import type { VendorTipsSheetProps } from "./VendorTipsSheet/VendorTipsSheet.types";
import type { VendorUploadMediaDrawerProps } from "./VendorUploadMediaDrawer/VendorUploadMediaDrawer.types";
import type { VerifyEmailModalProps } from "./VerifyEmailModal/VerifyEmailModal.types";

/** Every modal `AppModalsProvider` renders; each one's props are stored under its key. */
enum AppModalKey {
  // Global
  CloudinaryUploadModal = "cloudinary_upload",
  ImageGalleryModal = "image_gallery",
  PendingReviewsModal = "pending_reviews",
  PhoneVerificationModal = "phone_verification",
  PhotoViewerModal = "photo_viewer",
  ProfileModal = "profile",
  ShareModal = "share",
  VerifyEmailModal = "verify_email",

  // Home
  HomeCompactSearchTray = "home_compact_search_tray",
  HomeHeroFieldTray = "home_hero_field_tray",
  HomePromoEntitlementsModal = "home_promo_entitlements",

  // Host
  HostInterstitialModal = "host_interstitial",

  // Listings
  ListingAddOnModal = "listing_add_on",
  ListingAiAnswersModal = "listing_ai_answers",
  ListingAwardsWinnerDrawer = "listing_awards_winner",
  ListingFsvMenuModal = "listing_fsv_menu",
  ListingPhotoDownloadModal = "listing_photo_download",
  ListingPriceDetailsDrawer = "listing_price_details_drawer",
  ListingPriceDetailsModal = "listing_price_details",
  ListingPricingRulesModal = "listing_pricing_rules",
  ListingRecommendationsModal = "listing_recommendations",
  ListingSaveModal = "listing_save",

  // Account profile
  ProfileNameModal = "profile_name",
  ProfileOccupationModal = "profile_occupation",

  // Host profiles
  ProfilesMessageHostModal = "profiles_message_host",

  // Vendors
  VendorAboutModal = "vendor_about",
  VendorAddVideosModal = "vendor_add_videos",
  VendorContactSheet = "vendor_contact_sheet",
  VendorDeleteMediaModal = "vendor_delete_media",
  VendorDocumentModal = "vendor_document",
  VendorMediaTypeModal = "vendor_media_type",
  VendorPricingModal = "vendor_pricing",
  VendorReplacePhotoModal = "vendor_replace_photo",
  VendorTipsSheet = "vendor_tips_sheet",
  VendorUploadMediaDrawer = "vendor_upload_media",
}

/** What each modal is opened with. */
type AppModalProps = {
  // Global
  [AppModalKey.CloudinaryUploadModal]: CloudinaryUploadModalProps;
  [AppModalKey.ImageGalleryModal]: ImageGalleryModalProps;
  [AppModalKey.PendingReviewsModal]: PendingReviewsModalProps;
  [AppModalKey.PhoneVerificationModal]: PhoneVerificationModalProps;
  [AppModalKey.PhotoViewerModal]: PhotoViewerModalProps;
  [AppModalKey.ProfileModal]: ProfileModalProps;
  [AppModalKey.ShareModal]: ShareModalProps;
  [AppModalKey.VerifyEmailModal]: VerifyEmailModalProps;

  // Home
  [AppModalKey.HomeCompactSearchTray]: HomeCompactSearchTrayProps;
  [AppModalKey.HomeHeroFieldTray]: HomeHeroFieldTrayProps;
  [AppModalKey.HomePromoEntitlementsModal]: HomePromoEntitlementsModalProps;

  // Host
  [AppModalKey.HostInterstitialModal]: HostInterstitialModalProps;

  // Listings
  [AppModalKey.ListingAddOnModal]: ListingAddOnModalProps;
  [AppModalKey.ListingAiAnswersModal]: ListingAiAnswersModalProps;
  [AppModalKey.ListingAwardsWinnerDrawer]: ListingAwardsWinnerDrawerProps;
  [AppModalKey.ListingFsvMenuModal]: ListingFsvMenuModalProps;
  [AppModalKey.ListingPhotoDownloadModal]: ListingPhotoDownloadModalProps;
  [AppModalKey.ListingPriceDetailsDrawer]: ListingPriceDetailsDrawerProps;
  [AppModalKey.ListingPriceDetailsModal]: ListingPriceDetailsModalProps;
  [AppModalKey.ListingPricingRulesModal]: ListingPricingRulesModalProps;
  [AppModalKey.ListingRecommendationsModal]: ListingRecommendationsModalProps;
  [AppModalKey.ListingSaveModal]: ListingSaveModalProps;

  // Account profile
  [AppModalKey.ProfileNameModal]: ProfileNameModalProps;
  [AppModalKey.ProfileOccupationModal]: ProfileOccupationModalProps;

  // Host profiles
  [AppModalKey.ProfilesMessageHostModal]: ProfilesMessageHostModalProps;

  // Vendors
  [AppModalKey.VendorAboutModal]: VendorAboutModalProps;
  [AppModalKey.VendorAddVideosModal]: VendorAddVideosModalProps;
  [AppModalKey.VendorContactSheet]: VendorContactSheetProps;
  [AppModalKey.VendorDeleteMediaModal]: VendorDeleteMediaModalProps;
  [AppModalKey.VendorDocumentModal]: VendorDocumentModalProps;
  [AppModalKey.VendorMediaTypeModal]: VendorMediaTypeModalProps;
  [AppModalKey.VendorPricingModal]: VendorPricingModalProps;
  [AppModalKey.VendorReplacePhotoModal]: VendorReplacePhotoModalProps;
  [AppModalKey.VendorTipsSheet]: VendorTipsSheetProps;
  [AppModalKey.VendorUploadMediaDrawer]: VendorUploadMediaDrawerProps;
};

/**
 * One modal's slot. Closing keeps the props, so the modal stays mounted and plays its
 * exit; it is gone only once removed.
 */
type AppModalEntry<TModalKey extends AppModalKey> = {
  isOpen: boolean;
  /**
   * Unique per opening from closed, across removals too. Keys the modal, so each opening
   * mounts fresh.
   */
  openingId: number;
  props: AppModalProps[TModalKey];
};

/** Every modal opened since it was last removed, open or closing. */
type AppModalsState = { [TModalKey in AppModalKey]?: AppModalEntry<TModalKey> };

/**
 * Derives a modal's next props from its current ones, undefined while it is closed.
 * Returning the current props leaves the modal as it is.
 */
type AppModalPropsUpdater<TModalKey extends AppModalKey> = (
  existingModalProps: AppModalProps[TModalKey] | undefined,
) => AppModalProps[TModalKey];

/**
 * What `openModal` takes: props, or an updater. A modal whose props are all optional
 * can also be opened with nothing, which opens it with empty props.
 */
type AppModalOpenArgs<TModalKey extends AppModalKey> =
  // Empty props fit exactly when none are required.
  Record<never, never> extends AppModalProps[TModalKey]
    ? [nextProps?: AppModalProps[TModalKey] | AppModalPropsUpdater<TModalKey>]
    : [nextProps: AppModalProps[TModalKey] | AppModalPropsUpdater<TModalKey>];

/** How `AppModalsProvider` renders one modal. */
type AppModalConfig = {
  /** Reads its own open state and props through `useAppModal`, so it takes no props. */
  component: React.ComponentType;
};

/** What `AppModalsProvider` shares; read it through `useAppModal`, not directly. */
type AppModalsContextType = {
  modals: AppModalsState;
  /** Opens a modal with the given props, or replaces the props of an open one. */
  openModal: <TModalKey extends AppModalKey>(
    modal: TModalKey,
    props: AppModalProps[TModalKey] | AppModalPropsUpdater<TModalKey>,
  ) => void;
  /** Closes a modal, keeping it mounted with its props so it can animate out. */
  closeModal: (modal: AppModalKey) => void;
  /** Unmounts a modal at once and drops its props, open or not. */
  removeModal: (modal: AppModalKey) => void;
};

export { AppModalKey };
export type {
  AppModalConfig,
  AppModalEntry,
  AppModalOpenArgs,
  AppModalProps,
  AppModalPropsUpdater,
  AppModalsContextType,
  AppModalsState,
};
